import {ref, computed} from 'vue'

export const useAmenitiesManagement = (t?: any) => {
    // Amenities data
    const amenitiesData = ref([])
    const sustainableAmenitiesData = ref([])
    const sustainableAmenitiesLoading = ref(false)

    // Energy types for cycling
    const energyTypes = ['ELECTRICAL', 'GAS', 'OIL']

    // Energy type validation ranges and defaults
    // ZSH Override: Swapped Gas and Oil validation ranges and defaults
    const energyTypeConfig = {
        ELECTRICAL: {min: 0.26, max: 0.38, default: 0.32},
        GAS: {min: 0.2, max: 0.21, default: 0.2},
        OIL: {min: 0.27, max: 0.28, default: 0.27},
    }

    // Store modified amenity values
    const amenityModifications = ref({})

    // Track which amenities have had their kW values manually set by user
    const userModifiedKwValues = ref({})

    // Category display names - using i18n
    const categoryNames = computed(() => {
        if (!t || typeof t !== 'function') {
            // Fallback to English if t function is not provided
            return {
                ECO_AMENITIES: 'ECO Amenities',
                ENERGY_SAVING_SOLUTIONS: 'Energy consuming amenities',
                OTHER: 'All other amenities',
            }
        }
        try {
            return {
                ECO_AMENITIES: t('amenity_categories.ECO_AMENITIES'),
                ENERGY_SAVING_SOLUTIONS: t('amenity_categories.ENERGY_SAVING_SOLUTIONS'),
                OTHER: t('amenity_categories.OTHER'),
            }
        } catch (error) {
            // Fallback to English if translation fails
            return {
                ECO_AMENITIES: 'ECO Amenities',
                ENERGY_SAVING_SOLUTIONS: 'Energy consuming amenities',
                OTHER: 'All other amenities',
            }
        }
    })

    // Helper function to get amenity value (modified or original)
    const getAmenityValue = (amenityId: string | number, field: string) => {
        // Check if the field has been modified by the user (including empty strings)
        // Use hasOwnProperty to distinguish between undefined (never set) and any other value (including empty string)
        if (
            amenityModifications.value[amenityId] &&
            Object.prototype.hasOwnProperty.call(amenityModifications.value[amenityId], field)
        ) {
            const value = amenityModifications.value[amenityId][field]
            return value
        }

        // Only fall back to API defaults if the user has never touched this field
        const amenity = sustainableAmenitiesData.value.find(a => (a.amenityTypeId || a.id) === amenityId)
        if (amenity && amenity[field] !== undefined && amenity[field] !== null) {
            return amenity[field]
        }

        // For energyType field, default to 'ELECTRICAL' if no value is set and user hasn't modified it
        if (field === 'energyType') {
            return 'ELECTRICAL'
        }

        return null
    }

    // Helper function to set amenity value
    const setAmenityValue = (amenityId: string | number, field: string, value: any) => {
        if (!amenityModifications.value[amenityId]) {
            amenityModifications.value[amenityId] = {}
        }
        amenityModifications.value[amenityId][field] = value

        // Force reactivity by creating a new object reference
        amenityModifications.value = {...amenityModifications.value}
    }

    // Function to handle manual kW consumption changes by user
    const setAmenityKwConsumption = (amenityId: string | number, value: any) => {
        setAmenityValue(amenityId, 'kwconsumption', value)
        // Mark that user has manually modified the kW value
        userModifiedKwValues.value[amenityId] = true
    }

    // Helper function to convert string values to numbers for numeric fields
    const convertToNumber = (value: any): number | null => {
        if (value === null || value === undefined || value === '') {
            return null
        }
        const num = parseFloat(value)
        return isNaN(num) ? null : num
    }

    // Function to cycle energy type
    const cycleEnergyType = (amenityId: string | number) => {
        const currentType = getAmenityValue(amenityId, 'energyType')
        const currentIndex = energyTypes.indexOf(currentType)
        const nextIndex = (currentIndex + 1) % energyTypes.length
        const newEnergyType = energyTypes[nextIndex]

        // Mark that user has interacted with energy type BEFORE making any changes
        // This prevents kW updates on the first click
        userModifiedKwValues.value[amenityId] = true

        // Set the new energy type
        setAmenityValue(amenityId, 'energyType', newEnergyType)

        // No automatic kW updates since user is manually cycling energy types
    }

    // Function to get the proper kW consumption value for an amenity type
    const getAmenityTypeDefaultKwConsumption = (amenityTypeId: string | number) => {
        // Find the amenity type definition from the API data
        const amenityType = amenitiesData.value.find(a => a.id === amenityTypeId)
        if (amenityType && amenityType.kwconsumption !== null) {
            // Use the default kW consumption from the amenity type definition
            return amenityType.kwconsumption
        }
        // Fallback to electrical default if no specific value found
        return energyTypeConfig.ELECTRICAL.default
    }

    // Function to get the proper kW consumption value for an amenity type
    // Uses the amenity type's default value from the API
    const getSafeKwConsumption = (amenityTypeId: string | number) => {
        return getAmenityTypeDefaultKwConsumption(amenityTypeId)
    }

    // Function to initialize energy type when amenity is checked (no longer sets Usage and Watts)
    const initializeAmenityEnergyData = (amenityId: string | number) => {
        const amenity = sustainableAmenitiesData.value.find(a => (a.amenityTypeId || a.id) === amenityId)
        if (amenity && amenity.kwconsumption !== null) {
            // Only set energy type to ELECTRICAL when amenity is first checked
            // Do NOT set Usage Hours or kW consumption - those should only be set during form initialization
            if (!amenityModifications.value[amenityId] || !amenityModifications.value[amenityId]['energyType']) {
                setAmenityValue(amenityId, 'energyType', 'ELECTRICAL')
            }
        }
    }

    // Function to initialize default values from API during form initialization only
    const initializeAmenityDefaults = (amenityId: string | number) => {
        const amenity = sustainableAmenitiesData.value.find(a => (a.amenityTypeId || a.id) === amenityId)
        if (!amenity) return

        // Initialize amenityModifications for this amenity if it doesn't exist
        if (!amenityModifications.value[amenityId]) {
            amenityModifications.value[amenityId] = {}
        }

        // Set default values from API only if they haven't been set before
        if (
            amenity.usageHours !== undefined &&
            amenity.usageHours !== null &&
            !Object.prototype.hasOwnProperty.call(amenityModifications.value[amenityId], 'usageHours')
        ) {
            amenityModifications.value[amenityId]['usageHours'] = amenity.usageHours
        }

        if (
            amenity.kwconsumption !== undefined &&
            amenity.kwconsumption !== null &&
            !Object.prototype.hasOwnProperty.call(amenityModifications.value[amenityId], 'kwconsumption')
        ) {
            amenityModifications.value[amenityId]['kwconsumption'] = amenity.kwconsumption
        }
    }

    // Computed properties for grouped amenities
    const groupedAmenities = computed(() => {
        const groups = {}
        amenitiesData.value.forEach(amenity => {
            if (!groups[amenity.category]) {
                groups[amenity.category] = []
            }
            groups[amenity.category].push(amenity)
        })
        return groups
    })

    const amenityCategories = computed(() => {
        return Object.keys(groupedAmenities.value).sort()
    })

    // Computed properties for sustainable room amenities
    const groupedSustainableAmenities = computed(() => {
        const groups = {}
        // Only show amenities where basicEnabled = true
        const enabledAmenities = sustainableAmenitiesData.value.filter(amenity => amenity.basicEnabled === true)

        enabledAmenities.forEach(amenity => {
            if (!groups[amenity.category]) {
                groups[amenity.category] = []
            }
            groups[amenity.category].push(amenity)
        })
        return groups
    })

    const sustainableAmenityCategories = computed(() => {
        return Object.keys(groupedSustainableAmenities.value).sort()
    })

    // Computed properties for zero room amenities (basicEnabled = true)
    const groupedZeroAmenitiesBasic = computed(() => {
        const groups = {}
        // Only show amenities where basicEnabled = true
        const enabledAmenities = sustainableAmenitiesData.value.filter(amenity => amenity.basicEnabled === true)

        enabledAmenities.forEach(amenity => {
            if (!groups[amenity.category]) {
                groups[amenity.category] = []
            }
            groups[amenity.category].push(amenity)
        })
        return groups
    })

    const zeroAmenityCategoriesBasic = computed(() => {
        return Object.keys(groupedZeroAmenitiesBasic.value).sort()
    })

    // Computed properties for zero room amenities (basicEnabled = false)
    const groupedZeroAmenitiesExtra = computed(() => {
        const groups = {}
        // Only show amenities where basicEnabled = false
        const disabledAmenities = sustainableAmenitiesData.value.filter(amenity => amenity.basicEnabled === false)

        disabledAmenities.forEach(amenity => {
            if (!groups[amenity.category]) {
                groups[amenity.category] = []
            }
            groups[amenity.category].push(amenity)
        })
        return groups
    })

    const zeroAmenityCategoriesExtra = computed(() => {
        return Object.keys(groupedZeroAmenitiesExtra.value).sort()
    })

    return {
        // Data
        amenitiesData,
        sustainableAmenitiesData,
        sustainableAmenitiesLoading,
        amenityModifications,
        userModifiedKwValues,
        categoryNames,
        energyTypes,
        energyTypeConfig,

        // Functions
        getAmenityValue,
        setAmenityValue,
        setAmenityKwConsumption,
        convertToNumber,
        cycleEnergyType,
        getAmenityTypeDefaultKwConsumption,
        getSafeKwConsumption,
        initializeAmenityEnergyData,
        initializeAmenityDefaults,

        // Computed
        groupedAmenities,
        amenityCategories,
        groupedSustainableAmenities,
        sustainableAmenityCategories,
        groupedZeroAmenitiesBasic,
        zeroAmenityCategoriesBasic,
        groupedZeroAmenitiesExtra,
        zeroAmenityCategoriesExtra,
    }
}
